import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import EnquiryModal from "./EnquiryModal/EnquiryModal";
import { useTheme } from "@mui/material/styles";
import { CircularProgress, Typography, Box } from "@mui/material";
import { Icons } from "./icons.jsx";
import Form from "../Form/Form";
import Input from "../Form/Input/Input";
import SpotlightCard from "./SpotlightCard.jsx";
import ProgramCard from "./ProgramCard.jsx";
import MobileCarousel from "./MobileCarousel.jsx";
import {
  useSkillsAcademyPrograms,
  useSkillsAcademyCategories,
  SORT_TO_API,
} from "./useSkillsAcademy.js";
import useWindowWidth from "./useWindowWidth.js";
import {
  SkillsAcademySubheading,
  SkillsAcademyH1,
  SkillsAcademyIntro,
  SkillsAcademyStatItem,
  SkillsAcademyStatNum,
  SkillsAcademyChipButton,
  SkillsAcademyChipX,
  SkillsAcademyResultsText,
  SkillsAcademyResultsStrong,
  SkillsAcademyResultsEm,
  SkillsAcademySpotlightSection,
  SkillsAcademyEmptyState,
  SkillsAcademyEmptyTitle,
} from "./style.js";

import { DashLoader } from "../DashLoader/DashLoader";

const SORT_OPTIONS = [
  { label: "Most popular", value: "Most popular" },
  { label: "Highest rated", value: "Highest rated" },
  { label: "Newest", value: "Newest" },
];

const CATEGORY_OPTIONS = [
  { label: "All Categories", value: "All Categories" },
  { label: "Leadership", value: "Leadership" },
  { label: "Communication", value: "Communication" },
  { label: "Sales", value: "Sales" },
  { label: "Service", value: "Service" },
  { label: "Operations", value: "Operations" },
  { label: "Personal Growth", value: "Personal Growth" },
];

// ── Skills Academy listing page ──────────────────────────────────────────────────
const SkillsAcademyListing = () => {
  const { playToken, onAskPlay } = useOutletContext();
  const navigate = useNavigate();
  const [enquiryProgram, setEnquiryProgram] = useState(null);
  const onPreview = (program) => setEnquiryProgram(program);
  const onOpen = (program) => navigate(program.id);
  const theme = useTheme();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 640;
  const isFilterStacked = windowWidth < 798;

  const [query, setQuery] = useState("");
  const [chips, setChips] = useState(() => new Set(["All"]));
  const [category, setCategory] = useState("All Categories");
  const [sort, setSort] = useState("Most popular");

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  // Fetch categories from API
  const { data: categoriesData } = useSkillsAcademyCategories();
  const categoryObjects = categoriesData ?? [];
  const categoryNames = useMemo(
    () => categoryObjects.map((c) => c.name),
    [categoryObjects],
  );
  const QUICK_CHIPS = ["All", ...categoryNames];
  const categoryDropdownOptions = ["All Categories", ...categoryNames];

  // Resolve selected chips + dropdown to a list of category slugs for the API
  const activeCategorySlugs = useMemo(() => {
    const slugs = new Set();
    // Chips (multi-select)
    if (!chips.has("All")) {
      for (const name of chips) {
        const slug = categoryObjects.find((c) => c.name === name)?.slug;
        if (slug) slugs.add(slug);
      }
    }
    // Category dropdown (additive)
    if (category !== "All Categories") {
      const slug = categoryObjects.find((c) => c.name === category)?.slug;
      if (slug) slugs.add(slug);
    }
    return [...slugs];
  }, [chips, category, categoryObjects]);

  const filters = useMemo(
    () => ({
      search: debouncedSearch,
      categories: activeCategorySlugs,
      sort: SORT_TO_API[sort] ?? "popular",
    }),
    [debouncedSearch, activeCategorySlugs, sort],
  );

  const { data, isLoading, isError } = useSkillsAcademyPrograms(filters);
  const programs = data?.results ?? [];
  const stats = data?.stats ?? { program_count: 0, simulation_count: 0 };
  const totalCount = data?.count ?? 0;

  const toggleChip = useCallback((c) => {
    if (c === "All") {
      setChips(new Set(["All"]));
      return;
    }
    setChips((prev) => {
      const next = new Set(prev);
      if (next.has(c)) {
        next.delete(c);
        if (next.size === 0) return new Set(["All"]);
      } else {
        next.delete("All");
        next.add(c);
      }
      return next;
    });
  }, []);

  const spotlightProgram = programs.find((p) => p.is_featured) ?? null;
  const gridList = programs.filter((p) => p !== spotlightProgram);

  return (
    <>
      <div className="mp-wrap">
        <div className="mp-header">
          <div>
            <SkillsAcademySubheading>Curated by GuruNow</SkillsAcademySubheading>
            <SkillsAcademyH1>Skills Academy</SkillsAcademyH1>
            <SkillsAcademyIntro>
              Explore curated skill development programs designed to drive real
              behaviour change. Try any one in 5 minutes — no enrolment needed.
            </SkillsAcademyIntro>
          </div>
          <div className="mp-stats">
            {[
              [stats.program_count || totalCount, "programs"],
              [
                stats.simulation_count ? `${stats.simulation_count}+` : "—",
                "simulations",
              ],
              ["12k", "learners"],
            ].map(([num, lbl]) => (
              <SkillsAcademyStatItem key={lbl}>
                <SkillsAcademyStatNum>{num}</SkillsAcademyStatNum>
                {lbl}
              </SkillsAcademyStatItem>
            ))}
          </div>
        </div>

        <div className="mp-filter-bar">
          <div style={{ width: "100%" }}>
            <Form
              action="#"
              options={{
                defaultValues: {
                  search: "",
                  category: "All Categories",
                  sort: "Most popular",
                },
              }}
            >
              {(form) => (
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      minWidth: isFilterStacked ? "100%" : 220,
                    }}
                  >
                    <Input
                      id="search"
                      type="text"
                      dark
                      label="Search"
                      showLabel={false}
                      register={form.register("search")}
                      placeHolder="Search by program, topic, or Guru..."
                      onChange={(e) => setQuery(e.target.value)}
                      icon={
                        <Icons.search
                          size={18}
                          style={{ color: "#A87EFF", flexShrink: 0 }}
                        />
                      }
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      ...(isFilterStacked ? { flex: 1, width: "100%" } : {}),
                    }}
                  >
                    <div style={{ flex: isFilterStacked ? 1 : undefined }}>
                      <Input
                        id="category"
                        type="select"
                        label="Category"
                        showLabel={false}
                        register={form.register("category")}
                        control={form.control}
                        options={CATEGORY_OPTIONS}
                        onOptionSelect={setCategory}
                        placeHolder="All Categories"
                      />
                    </div>
                    <div style={{ flex: isFilterStacked ? 1 : undefined }}>
                      <Input
                        id="sort"
                        type="select"
                        label="Sort"
                        showLabel={false}
                        register={form.register("sort")}
                        control={form.control}
                        options={SORT_OPTIONS}
                        onOptionSelect={setSort}
                        placeHolder="Most popular"
                      />
                    </div>
                  </div>
                </div>
              )}
            </Form>
          </div>
        </div>

        <div className="mp-chips-row">
          {QUICK_CHIPS.map((c) => {
            const isSelected = chips.has(c);
            return (
              <SkillsAcademyChipButton
                key={c}
                $isSelected={isSelected}
                onClick={() => toggleChip(c)}
              >
                {c}
                {isSelected && c !== "All" && (
                  <SkillsAcademyChipX>✕</SkillsAcademyChipX>
                )}
              </SkillsAcademyChipButton>
            );
          })}
        </div>

        {!isLoading ? (
          <SkillsAcademyResultsText>
            Showing{" "}
            <SkillsAcademyResultsStrong>
              {programs.length}
            </SkillsAcademyResultsStrong>{" "}
            of {totalCount} programs
            {debouncedSearch && (
              <>
                {" "}
                for "
                <SkillsAcademyResultsEm>{debouncedSearch}</SkillsAcademyResultsEm>"
              </>
            )}
          </SkillsAcademyResultsText>
        ) : (
          <div style={{ padding: "8px 0" }}></div>
        )}

        {isLoading && <DashLoader text="Loading programs..." />}

        {isError && !isLoading && (
          <div
            style={{ textAlign: "center", padding: "60px 0", color: "#D1C7FF" }}
          >
            <p>Failed to load programs. Please try again.</p>
          </div>
        )}

        {!isLoading && !isError && (
          <>
            {spotlightProgram && (
              <SkillsAcademySpotlightSection>
                <SpotlightCard
                  program={spotlightProgram}
                  playToken={playToken}
                  onAskPlay={onAskPlay}
                  onPreview={onPreview}
                  onOpen={onOpen}
                />
              </SkillsAcademySpotlightSection>
            )}
            {isMobile ? (
              <MobileCarousel
                programs={programs}
                playToken={playToken}
                onAskPlay={onAskPlay}
                onPreview={onPreview}
                onOpen={onOpen}
              />
            ) : (
              <section className="mp-grid">
                {gridList.map((p) => (
                  <ProgramCard
                    key={p.id}
                    program={p}
                    playToken={playToken}
                    onAskPlay={onAskPlay}
                    onPreview={onPreview}
                    onOpen={onOpen}
                  />
                ))}
              </section>
            )}

            {programs.length === 0 && (
              <SkillsAcademyEmptyState>
                <Icons.search
                  size={32}
                  style={{ color: theme.palette.text.brand_secondary }}
                />
                <SkillsAcademyEmptyTitle>
                  No programs match that.
                </SkillsAcademyEmptyTitle>
                <p>
                  Try clearing filters or searching for "leadership", "sales",
                  or "communication".
                </p>
              </SkillsAcademyEmptyState>
            )}
          </>
        )}
      </div>

      {enquiryProgram && (
        <EnquiryModal
          program={enquiryProgram}
          onClose={() => setEnquiryProgram(null)}
        />
      )}
    </>
  );
};

export default SkillsAcademyListing;
